import { useDispatch } from 'react-redux'
import {
  showModal,
  hideModal,
  showSuccesNotification,
  showErrorNotification,
} from '../../../../../../redux/actions'
import {
  useAddGovxShopIntegrationMutation,
  useUpdateGovxShopIntegrationMutation,
  useGetAvailableAppIntegrationsQuery,
  useGetPartnerListingIntegrationsQuery,
  useRemoveListingIntegrationMutation,
  useLazyFetchListingCustomerQuery,
  useDeleteListingCustomerMutation,
  useSaveListingCustomerMutation,
  useCreateListingCustomerMutation,
} from "../../../../../../redux/slices/integrationsSlice"
import {
  useGetPartnerListingQuery,
} from '../../../../Listings/partnerListingsSlice'
import {
  useLazySearchMarketplaceCustomerQuery,
} from '../../../../../../redux/slices/partnersSlice'
import {
  useLazySearchManufacturersQuery,
  useLazyGetStoreByIdQuery,
} from '../../../../../../redux/slices/catalogSlice'

const useIntegrations = (partnerListingId) => {
  const dispatch = useDispatch()
  const {
    data: availableAppIntegrations,
    isLoading: isLoadingAvailableAppIntegrations,
    isError: isErrorAvailableAppIntegrations,
  } = useGetAvailableAppIntegrationsQuery()

  const {
    data: partnerListingIntegrations,
    isLoading: isLoadingPartnerListingIntegrations,
    isError: isErrorPartnerListingIntegrations,
    refetch: refetchPartnerListingIntegrations,
  } = useGetPartnerListingIntegrationsQuery(partnerListingId)

  const { data: partnerListing, isSuccess, refetch: refetchPartnerListing } = useGetPartnerListingQuery(partnerListingId)
  const [addGovxShopIntegration] = useAddGovxShopIntegrationMutation()
  const [updateGovxShopIntegration] = useUpdateGovxShopIntegrationMutation()
  const [removePartnerListingIntegration] = useRemoveListingIntegrationMutation()
  const [getListingCustomer] = useLazyFetchListingCustomerQuery()
  const [deleteListingCustomer] = useDeleteListingCustomerMutation()
  const [saveListingCustomer] = useSaveListingCustomerMutation()
  const [createListingCustomer] = useCreateListingCustomerMutation()
  const [searchMarketplaceCustomer] = useLazySearchMarketplaceCustomerQuery()
  const [searchManufacturers] = useLazySearchManufacturersQuery()
  const [getStoreById] = useLazyGetStoreByIdQuery()

  const integrationTypes = {
    NetSuite: 'NetSuite',
    GovXShop: 'GovxShop'
  }

  const createPartnerListingGovxShopIntegration = async (manufacturerId, dateLinkedUtc, storeIds = []) => {
    try {
      await addGovxShopIntegration({
        partnerListingId,
        manufacturerId,
        dateLinkedUtc,
        storeIds,
      }).unwrap()
      dispatch(showSuccesNotification('GOVX Shop connection was successfully created.'))
      refetchPartnerListingIntegrations()
    } catch (err) {
      dispatch(showErrorNotification('Something went wrong connecting this seller to GOVX Shop. Please try again later.'))
      console.error(err)
      throw err
    }
  }

  const updatePartnerListingGovxShopIntegration = async (manufacturerId, storeIds = []) => {
    try {
      await updateGovxShopIntegration({
        partnerListingId,
        manufacturerId,
        storeIds,
      }).unwrap()
      dispatch(showSuccesNotification('GOVX Shop connection was successfully updated.'))
      refetchPartnerListingIntegrations()
    } catch (err) {
      dispatch(showErrorNotification('Something went wrong updating the GOVX Shop connection. Please try again later.'))
      console.error(err)
      throw err
    }
  }

  const deletePartnerListingIntegration = async (integrationType,) => {
    try {
      await removePartnerListingIntegration({ partnerListingId, integrationType }).unwrap()
      refetchPartnerListingIntegrations()
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  const openListingItegrationModal = () => {
    getListingCustomer(partnerListingId).then((res) => {
      dispatch(showModal('LISTING_INTEGRATION_MODAL', {
        partnerListing,
        customer: res.isError && res.error.status === 404 ? null : res.data,
        deleteListingCustomer,
        searchMarketplaceCustomer,
        saveListingCustomer,
        createListingCustomer,
        refetchPartnerListingIntegrations,
      }))
    })
  }

  const viewListingIntegration = () => {
    openListingItegrationModal()
  }

  const removeGovxShopConnection = async () => {
    try {
      await removePartnerListingIntegration({
        partnerListingId,
        integrationType: integrationTypes.GovXShop,
      }).unwrap()
      dispatch(showSuccesNotification('GOVX Shop connection successfully removed.'))
      refetchPartnerListingIntegrations()
    } catch (err) {
      dispatch(showErrorNotification('Something went wrong removing the GOVX Shop connection. Please try again later.'))
      console.error(err)
      throw err
    }
  }

  const openRemoveConnectionModal = (row) => {
    if (row?.type === integrationTypes.NetSuite) {
      openListingItegrationModal()
      return
    }

    dispatch(showModal('DIALOG', {
      title: 'Remove Connection',
      confirmLabel: 'Remove',
      cancelLabel: 'Cancel',
      onClickConfirm: removeGovxShopConnection,
      onClickCancel: () => dispatch(hideModal()),
      closeModal: () => dispatch(hideModal()),
      children: 'Are you sure you want to remove the connection to GOVX Shop? Deleting the link will impact products and reporting.',
    }))
  }

  const openManageConnectionModal = async (integration, app) => {
    const res = await searchManufacturers({ manufacturerId: Number(integration.manufacturerId) })
    const manufacturer = res.data?.length ? res.data[0] : null
    let stores = manufacturer?.stores || []

    // For any storeId in the integration that wasn't returned by the search,
    // fetch it individually so existing connections still display.
    const linkedStoreIds = integration.storeIds || []
    const returnedStoreIds = new Set(stores.map((s) => String(s.storeId)))
    const missingStoreIds = linkedStoreIds.filter((id) => !returnedStoreIds.has(String(id)))

    if (missingStoreIds.length > 0) {
      const results = await Promise.all(
        missingStoreIds.map((id) => getStoreById(id).unwrap().catch(() => null))
      )
      results.forEach((store) => {
        if (store) {
          stores = [...stores, { storeId: store.id, storeName: store.name }]
        }
      })
    }

    const orphanedStoreIds = new Set(missingStoreIds.map(String))

    dispatch(showModal('MANAGE_CONNECTION_MODAL', {
      partnerListingId,
      integration,
      app: app || {},
      manufacturerStores: stores,
      orphanedStoreIds: [...orphanedStoreIds],
    }))
  }

  return {
    partnerListingIntegrations,
    isLoadingPartnerListingIntegrations,
    isErrorPartnerListingIntegrations,
    availableAppIntegrations,
    isLoadingAvailableAppIntegrations,
    isErrorAvailableAppIntegrations,
    createPartnerListingGovxShopIntegration,
    updatePartnerListingGovxShopIntegration,
    deletePartnerListingIntegration,
    viewListingIntegration,
    openManageConnectionModal,
    openRemoveConnectionModal,
    integrationTypes,
  }
}

export default useIntegrations
